import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Paper, Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, sectionHeadingSx, actionRowSx,
} from '../portalStyles';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { enableStackSets, listStackSetsAccounts } from '../../../services/stacksetsApi';

const STEPS = ['Admin account', 'Review & enable'];

// One measure for every wizard step body so the form does not re-flow its
// width as the user advances (shared with StackSetCreate / StackSetEdit).
const STEP_MAX_WIDTH = 720;

// Admin-console stepper: Metropolis labels, accent for the active step.
const stepperSx = {
  mb: 3,
  overflowX: 'auto',
  '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 500, color: CK.body },
  '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
  '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
  '& .MuiStepIcon-root.Mui-active': { color: CK.blue },
  '& .MuiStepIcon-root.Mui-completed': { color: CK.green },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };

// Enablement wizard: pick the admin account, review what Prism will create,
// submit. The entry gate takes over polling once status flips to in_progress.
export default function StackSetsSetup({ onComplete, priorFailure }) {
  const [activeStep, setActiveStep] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [adminAccount, setAdminAccount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await listStackSetsAccounts();
        setAccounts(res?.data || []);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  const accountLabel = (a) => `${a.accountName || a.name || a.accountId} (${a.accountId})`;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await enableStackSets(adminAccount.accountId);
      onComplete();
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<LayersIcon sx={{ fontSize: 24 }} />}
          title="Enable CloudFormation StackSets"
          subtitle="Deploy CloudFormation templates to every account under your organization's OUs"
        />
      </motion.div>
      {priorFailure && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
            The previous setup attempt failed: {priorFailure}. You can retry below.
          </Alert>
        </motion.div>
      )}
      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>
        </motion.div>
      )}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          <Stepper activeStep={activeStep} sx={stepperSx}>
            {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>

          {activeStep === 0 && (
            <Box sx={{ maxWidth: STEP_MAX_WIDTH }}>
              <Typography sx={{ ...bodyTextSx, mb: 2 }}>
                The admin account hosts your StackSets, the administration role, and the
                template bucket. Every other account gets an execution role that trusts it.
              </Typography>
              <Autocomplete
                options={accounts}
                getOptionLabel={accountLabel}
                value={adminAccount}
                onChange={(_, v) => setAdminAccount(v)}
                renderInput={(params) => <TextField {...params} label="StackSets admin account" />}
                sx={{ maxWidth: 480 }}
              />
              <Box sx={{ ...actionRowSx, mt: 3 }}>
                <Button variant="contained" disabled={!adminAccount} onClick={() => setActiveStep(1)}>
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ maxWidth: STEP_MAX_WIDTH }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Prism will create:</Typography>
              <Box component="ul" sx={{ ...bodyTextSx, pl: 3, my: 0 }}>
                <li>
                  CKPrism-StackSetAdministrationRole + a private template bucket in
                  {' '}{adminAccount ? accountLabel(adminAccount) : ''}
                </li>
                <li>
                  CKPrism-StackSetExecutionRole (AdministratorAccess) in every onboarded
                  account, trusting the administration role
                </li>
              </Box>
              <Alert severity="info" sx={{ my: 2, borderRadius: '8px' }}>
                No stacks are deployed yet — you create StackSets after enablement.
              </Alert>
              <Box sx={actionRowSx}>
                <Button onClick={() => setActiveStep(0)}>Back</Button>
                <Button variant="contained" disabled={submitting} onClick={submit}>
                  {submitting ? 'Enabling…' : 'Enable StackSets'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </motion.div>
    </motion.div>
  );
}
